import type { SolarPvViewId } from './solarInstallationsNarrative';
import type { DgStatsCoverageFacts } from '../../services/solarDgStatsCoverage';
import { DGSTATS_COVERAGE_UNAVAILABLE_LABEL } from '../../services/solarDgStatsCoverage';
import { SOLAR_INSTALLER_MIN_SAMPLE } from './solarCostStatistics';
import { SOLAR_ANALYSIS_CUTOFF_YEAR } from '../../services/solarAnalysisCutoff';

export type SolarInstallationNarrativeFacts = {
  locationName: string;
  viewId: SolarPvViewId;
  coverage: DgStatsCoverageFacts;
  latestYear: number | null;
  capacityMw: number | null;
  latestAnnualCapacityMw: number | null;
  cumulativeSolarMw: number | null;
  localSolarKwPerResident: number | null;
  sameYearPopulation: number | null;
  aggregateSolarCostPerW: number | null;
  representedProjectCount: number | null;
  projectUnitLabel: string;
  validApplicationCount: number | null;
  selectedSolarSectorLabel: string;
  solarOnlyCostPerKw: number | null;
  solarStorageCostPerKw: number | null;
  solarStorageCostUnitLabel: string;
  comparisonYear: number | null;
  projectCostDifferencePerW: number | null;
  solarOnlySampleN: number | null;
  solarStorageSampleN: number | null;
  topInstallerLabel: string;
  topInstallerShare: number | null;
  installerShareBasis: string;
  secondInstallerSentence: string;
  installerCountSentence: string;
  lowestCostInstaller: string;
  highestCostInstaller: string;
  lowestInstallerCostPerW: number | null;
  highestInstallerCostPerW: number | null;
  lowestInstallerN: number | null;
  highestInstallerN: number | null;
  comparisonUniverseLabel: string;
  minimumInstallerSample: number;
  installerCostSampleSentence: string;
  trendSentence: string;
  sectorShareSentence: string;
  solarPerCapitaTrendSentence: string;
  solarPerCapitaComparisonSentence: string;
  solarCostTrendSentence: string;
  solarCostComparisonSentence: string;
  solarSampleSentence: string;
  ownershipMixSentence: string;
  cumulativeGrowthSentence: string;
  latestAdditionSentence: string;
  projectCostDifferenceSentence: string;
  sampleSentence: string;
  coverageSentence: string;
  nominalDollarsNote: string;
  solarCutoffYear: number | null;
  populationYear: number | null;
};

export function buildSolarInstallationNarrativeFacts(
  input: Partial<SolarInstallationNarrativeFacts> & {
    locationName: string;
    viewId: SolarPvViewId;
    coverage: DgStatsCoverageFacts;
  }
): SolarInstallationNarrativeFacts {
  const coverageSentence =
    input.coverage.coverageStatus === 'UNAVAILABLE' ||
    input.coverage.coverageStatus === 'INCOMPLETE'
      ? ` ${input.coverage.coverageReason || DGSTATS_COVERAGE_UNAVAILABLE_LABEL}`
      : input.coverageSentence ?? '';

  const projectCostDifferenceSentence =
    input.solarOnlyCostPerKw != null &&
    input.solarStorageCostPerKw != null &&
    input.comparisonYear != null
      ? ` The solar-plus-storage group was ${Math.abs(input.solarStorageCostPerKw - input.solarOnlyCostPerKw).toFixed(0)} ${input.solarStorageCostUnitLabel ?? '$/kW'} ${input.solarStorageCostPerKw >= input.solarOnlyCostPerKw ? 'higher' : 'lower'} than solar-only in ${input.comparisonYear} in the displayed source records. The difference is descriptive and is not an estimate of battery cost.`
      : '';

  return {
    minimumInstallerSample: SOLAR_INSTALLER_MIN_SAMPLE,
    nominalDollarsNote: 'Nominal $/W DC (not inflation-adjusted).',
    selectedSolarSectorLabel: input.selectedSolarSectorLabel ?? 'included sectors',
    topInstallerLabel: input.topInstallerLabel ?? '—',
    installerShareBasis:
      input.installerShareBasis ??
      'capacity represented by the displayed top 10 installer-manufacturer pairs',
    projectUnitLabel: input.projectUnitLabel ?? 'applications',
    secondInstallerSentence: input.secondInstallerSentence ?? '',
    installerCountSentence: input.installerCountSentence ?? '',
    lowestCostInstaller: input.lowestCostInstaller ?? '—',
    highestCostInstaller: input.highestCostInstaller ?? '—',
    installerCostSampleSentence: input.installerCostSampleSentence ?? '',
    trendSentence: input.trendSentence ?? '',
    sectorShareSentence: input.sectorShareSentence ?? '',
    solarPerCapitaTrendSentence: input.solarPerCapitaTrendSentence ?? '',
    solarPerCapitaComparisonSentence: input.solarPerCapitaComparisonSentence ?? '',
    solarCostTrendSentence: input.solarCostTrendSentence ?? '',
    solarCostComparisonSentence: input.solarCostComparisonSentence ?? '',
    solarSampleSentence: input.solarSampleSentence ?? '',
    ownershipMixSentence: input.ownershipMixSentence ?? '',
    cumulativeGrowthSentence: input.cumulativeGrowthSentence ?? '',
    latestAdditionSentence: input.latestAdditionSentence ?? '',
    sampleSentence: input.sampleSentence ?? '',
    projectCostDifferenceSentence,
    coverageSentence,
    latestYear: input.latestYear ?? null,
    capacityMw: input.capacityMw ?? null,
    latestAnnualCapacityMw: input.latestAnnualCapacityMw ?? null,
    cumulativeSolarMw: input.cumulativeSolarMw ?? null,
    localSolarKwPerResident: input.localSolarKwPerResident ?? null,
    sameYearPopulation: input.sameYearPopulation ?? null,
    comparisonUniverseLabel: input.comparisonUniverseLabel ?? '',
    lowestInstallerN: input.lowestInstallerN ?? null,
    highestInstallerN: input.highestInstallerN ?? null,
    aggregateSolarCostPerW: input.aggregateSolarCostPerW ?? null,
    representedProjectCount: input.representedProjectCount ?? null,
    validApplicationCount: input.validApplicationCount ?? input.representedProjectCount ?? null,
    solarOnlyCostPerKw: input.solarOnlyCostPerKw ?? null,
    solarStorageCostPerKw: input.solarStorageCostPerKw ?? null,
    solarStorageCostUnitLabel: input.solarStorageCostUnitLabel ?? '$/kW (mean, residential tech_type_cost)',
    comparisonYear: input.comparisonYear ?? null,
    projectCostDifferencePerW: input.projectCostDifferencePerW ?? null,
    solarOnlySampleN: input.solarOnlySampleN ?? null,
    solarStorageSampleN: input.solarStorageSampleN ?? null,
    topInstallerShare: input.topInstallerShare ?? null,
    lowestInstallerCostPerW: input.lowestInstallerCostPerW ?? null,
    highestInstallerCostPerW: input.highestInstallerCostPerW ?? null,
    locationName: input.locationName,
    viewId: input.viewId,
    coverage: input.coverage,
    solarCutoffYear: input.solarCutoffYear ?? SOLAR_ANALYSIS_CUTOFF_YEAR,
    populationYear: input.populationYear ?? input.latestYear ?? null,
  };
}
